<script setup lang="ts">
import type { IWidgetUser } from '../types'
import { UserRound, Mail, Phone, Smartphone, Globe } from 'lucide-vue-next'

const props = withDefaults(defineProps<IWidgetUser>(), {
  variant: 'white',
  hideContacts: false,
  showNews: false
})
</script>

<template>
  <div
    class="w-full rounded-lg p-4"
    :class="props.variant === 'white' ? 'bg-white' : 'bg-gray-100'"
  >
    <div class="flex gap-4 sm:gap-2">
      <div>
        <NuxtImg
          v-if="props.avatar"
          :src="props.avatar"
          :alt="props.fullname"
          width="56"
          height="56"
          size="56px"
          format="avif,webp"
          class="h-14 w-14 min-w-14 rounded-[6px] transition-transform duration-300 hover:z-10 hover:scale-[3] hover:rounded-[4px]"
        />
        <div
          v-else
          class="flex h-14 w-14 shrink-0 items-center justify-center rounded-lg bg-white transition-transform duration-300 hover:z-10 hover:scale-[3] hover:rounded-[4px]"
        >
          <UserRound />
        </div>
      </div>

      <div class="grid flex-1 grid-cols-1 gap-2 sm:grid-cols-2">
        <div>
          <NuxtLinkLocale
            v-if="props.url"
            :to="props.url"
            class="font-serif font-bold leading-4 hover:underline"
            >{{ props.fullname }}</NuxtLinkLocale
          >
          <div v-else class="font-serif font-bold leading-4">{{ props.fullname }}</div>
          <div v-if="props.description" class="mt-2 text-sm leading-3 text-gray-700">
            {{ props.description }}
          </div>
        </div>
        <div class="flex-shrink-0 sm:mt-2">
          <div v-if="props.showNews" class="text-xs text-gray-600">
            {{ $t('common.publications_count') }} {{ props.news_count || 0 }}
          </div>
          <div v-if="props.contacts?.length && !hideContacts" class="mt-2 flex flex-wrap gap-3">
            <template v-for="(contact, index) in props.contacts" :key="index">
              <a
                v-if="'email' in contact"
                :href="`mailto:${contact.email}`"
                target="_blank"
                :aria-label="$t('common.send.email.to', { email: contact.email })"
                class="text-blue-600 hover:text-blue-700"
              >
                <Mail class="h-4 w-4" />
              </a>

              <a
                v-else-if="'telegram' in contact"
                :href="`https://t.me/${contact.telegram}`"
                target="_blank"
                :aria-label="$t('common.open.telegram.chat', { username: contact.telegram })"
                class="text-blue-600 hover:text-blue-700"
              >
                <i class="[&>svg]:h-4 [&>svg]:w-4"><UiIcon name="telegram" /></i>
              </a>

              <a
                v-else-if="'whatsapp' in contact"
                :href="`https://wa.me/${contact.whatsapp}`"
                target="_blank"
                :aria-label="$t('common.open.whatsapp.chat', { phone: contact.whatsapp })"
                class="text-blue-600 hover:text-blue-700"
              >
                <i class="[&>svg]:h-4 [&>svg]:w-4"><UiIcon name="whatsapp" /></i>
              </a>

              <a
                v-else-if="'mobile_phone' in contact || 'office_phone' in contact"
                :href="`tel:${contact.mobile_phone || contact.office_phone}`"
                :aria-label="
                  $t('common.call.phone', { phone: contact.mobile_phone || contact.office_phone })
                "
                class="text-blue-600 hover:text-blue-700"
              >
                <Smartphone v-if="contact.mobile_phone" class="h-4 w-4" />
                <Phone v-else class="h-4 w-4" />
              </a>

              <a
                v-else-if="'website_url' in contact"
                :href="contact.website_url"
                target="_blank"
                rel="noopener noreferrer"
                :aria-label="$t('common.open.website', { website: contact.website_url })"
                class="text-blue-600 hover:text-blue-700"
              >
                <Globe class="h-4 w-4" />
              </a>
            </template>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
